import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { AppState } from 'app/types';

import { MultimediaRoomService } from '../services/multimedia-room.service';
import { SceneService } from '../services/scene.service';
import { BrowserService } from '../services/browser.service';
import { SceneManager } from '../social-pixi-canvas/scene-manager';
import { ZoomDirection } from '../social-pixi-canvas/zoom-controller';

@Component({
  selector: 'app-zoom-pixi-world-controls',
  templateUrl: './zoom-pixi-world-controls.component.html',
  styleUrls: ['./zoom-pixi-world-controls.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZoomPixiWorldControlsComponent implements OnDestroy {
  private sceneService = inject(SceneService);
  private browserService = inject(BrowserService);
  private store = inject<Store<AppState>>(Store);
  private multimediaRoomService = inject(MultimediaRoomService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  private destroyed$ = new ReplaySubject<boolean>(1);

  public readonly showZoomControls = input(true);

  public readonly showZoomFlag = signal(true);
  public readonly autoZoomFlag = signal(false);
  public readonly autoZoomEnabled = signal(true);
  public readonly inDisabled = signal(false);
  public readonly outDisabled = signal(false);
  public readonly zoomDirection = signal<ZoomDirection>({ value: 'none' });
  protected readonly pinchZoomEnabled = signal(false);
  private readonly socialAudioRelative = signal(false);
  private sceneManagerSubscriptionsSetup = false;
  public readonly isMobile = signal(this.browserService?.isMobileOrTablet ?? false);

  public readonly shouldShowControls = computed(() => this.showZoomControls() && this.showZoomFlag());
  public readonly isPinchZoomActive = computed(() => this.pinchZoomEnabled() && this.isMobile());
  public readonly zoomInButtonClasses = computed(() => ({
    active: this.zoomDirection().value === 'in',
    disabled: !this.autoZoomEnabled() && this.inDisabled(),
  }));
  public readonly zoomOutButtonClasses = computed(() => ({
    active: this.zoomDirection().value === 'out',
    disabled: !this.autoZoomEnabled() && this.outDisabled(),
  }));
  public readonly autoZoomTooltip = computed(() => `Auto Zoom is ${this.autoZoomEnabled() ? 'on' : 'off'}`);
  public readonly isSceneManagerAvailable = computed(() => !!this.sceneService?.sceneManager);

  // Create computed signals to track zoom state
  public readonly isAtMinZoom = computed(() => this.outDisabled());
  public readonly isAtMaxZoom = computed(() => this.inDisabled());

  public constructor() {
    const sceneService = this.sceneService;

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_zoom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showZoomFlag) => this.showZoomFlag.set(showZoomFlag));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.enable_auto_zoom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((autoZoomFlag) => {
        this.autoZoomFlag.set(autoZoomFlag);
        this.autoZoomEnabled.set(autoZoomFlag);

        if (this.sceneService.sceneManager) {
          this.sceneService.sceneManager.autoZoomEnabled = autoZoomFlag;
          this.sceneService.sceneManager.autoZoomEnabled$.next(autoZoomFlag);
        }
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.pinch_zoom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((pinchZoom) => this.pinchZoomEnabled.set(pinchZoom));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_audio_relative)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((socialAudioRelative) => this.socialAudioRelative.set(socialAudioRelative));

    // Set up a polling interval to check for scene manager availability
    const checkSceneManagerInterval = setInterval(() => {
      if (sceneService.sceneManager && !this.sceneManagerSubscriptionsSetup) {
        this.setupSceneManagerSubscriptions(sceneService.sceneManager);
        clearInterval(checkSceneManagerInterval);
      }
    }, 100);

    this.destroyed$.subscribe(() => {
      clearInterval(checkSceneManagerInterval);
    });
  }

  /**
   * Setup subscriptions for scene manager events
   * @param sceneManager The scene manager instance
   */
  private setupSceneManagerSubscriptions(sceneManager: SceneManager): void {
    if (this.sceneManagerSubscriptionsSetup) {
      return;
    }

    // Subscribe to auto zoom state changes
    sceneManager.autoZoomEnabled$
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((autoZoomEnabled: boolean) => this.autoZoomEnabled.set(autoZoomEnabled));

    // Subscribe to zoom direction changes for UI updates
    sceneManager.zoomDirection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value: ZoomDirection) => this.zoomDirection.set(value));

    this.checkBounds();

    this.sceneManagerSubscriptionsSetup = true;
  }

  public toggleAutoZoom(forceOff = false): void {
    const sm = this.sceneService.sceneManager;
    if (!sm) {
      return;
    }

    // Toggle auto zoom in the scene manager
    this.autoZoomEnabled.set(sm.toggleAutoZoom(forceOff));

    // When enabling auto zoom, reset the disabled state of the zoom buttons
    if (this.autoZoomEnabled()) {
      // Reset disabled states
      this.inDisabled.set(false);
      this.outDisabled.set(false);
    } else {
      // Re-check the bounds to update disabled states based on current zoom
      this.checkBounds();
    }

    // Ensure UI updates
    this.changeDetectorRef.markForCheck();
  }

  public checkBounds(): void {
    const sm = this.sceneService.sceneManager;
    if (!sm) {
      return;
    }

    // Use a small epsilon value to handle floating point precision issues
    const epsilon = 0.01;

    this.inDisabled.set(sm.currentZoom >= sm.maxZoom - epsilon);
    this.outDisabled.set(sm.currentZoom <= sm.zoomController.options.minZoom + epsilon);
    this.changeDetectorRef.markForCheck();
  }

  public startZoom(zoomIn: boolean): void {
    const sm = this.sceneService?.sceneManager;
    if (!sm) {
      return;
    }

    // Start continuous zoom - auto zoom disabling is handled in scene manager
    sm.startContinuousZoom(zoomIn);

    if (this.autoZoomEnabled()) {
      this.autoZoomEnabled.set(false);
    }
    this.checkBounds();
  }

  public stopZoom(): void {
    const sm = this.sceneService?.sceneManager;
    if (!sm) {
      return;
    }

    sm.stopContinuousZoom();
    this.checkBounds();

    // Update audio if needed
    if (this.socialAudioRelative()) {
      this.multimediaRoomService.setAllVideoParticipantsSound();
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
